import React from "react";
import { PaziaSelect, IDropdownOption } from "components/atoms";
import { components } from "react-select";

/**
 * MaterialIconSelect Component
 *
 * A dropdown component for selecting Material Icons. The selected icon value can be used
 * to render HTML like: <span className="material-icons">{iconValue}</span>
 *
 * This component integrates with react-hook-form and provides a searchable dropdown
 * with preview of each icon alongside its name.
 *
 * @example
 * <MaterialIconSelect name="icon" label="Choose Icon" />
 *
 * The form will store the icon value (e.g., "payments", "info", etc.) which can be
 * used directly in Material Icons spans or processed to generate the full HTML.
 */

// Common Material Icons that might be useful for course fields
const MATERIAL_ICONS: IDropdownOption[] = [
  { value: "", label: "Brak ikony" },

  // Document & Content
  { value: "description", label: "Opis (description)" },
  { value: "article", label: "Artykuł (article)" },
  { value: "note", label: "Notatka (note)" },
  { value: "notes", label: "Notatki (notes)" },
  { value: "text_snippet", label: "Fragment tekstu (text_snippet)" },
  { value: "menu_book", label: "Menu książka (menu_book)" },
  { value: "book", label: "Książka (book)" },
  { value: "library_books", label: "Biblioteka książek (library_books)" },

  // Information & Help
  { value: "info", label: "Informacje (info)" },
  { value: "info_outline", label: "Informacje kontur (info_outline)" },
  { value: "help", label: "Pomoc (help)" },
  { value: "help_outline", label: "Pomoc kontur (help_outline)" },
  { value: "contact_support", label: "Kontakt wsparcie (contact_support)" },
  { value: "support", label: "Wsparcie (support)" },
  { value: "live_help", label: "Pomoc na żywo (live_help)" },

  // Education & Learning
  { value: "school", label: "Szkoła (school)" },
  { value: "class", label: "Klasa (class)" },
  { value: "quiz", label: "Quiz (quiz)" },
  { value: "assignment", label: "Zadanie (assignment)" },
  { value: "assignment_turned_in", label: "Zadanie oddane (assignment_turned_in)" },
  { value: "grade", label: "Ocena (grade)" },
  { value: "psychology", label: "Psychologia (psychology)" },
  { value: "science", label: "Nauka (science)" },
  { value: "biotech", label: "Biotechnologia (biotech)" },

  // Payment & Money
  { value: "payments", label: "Płatności (payments)" },
  { value: "payment", label: "Płatność (payment)" },
  { value: "money", label: "Pieniądze (money)" },
  { value: "attach_money", label: "Dolar (attach_money)" },
  { value: "euro", label: "Euro (euro)" },
  { value: "currency_exchange", label: "Wymiana walut (currency_exchange)" },
  { value: "monetization_on", label: "Monetyzacja (monetization_on)" },
  { value: "account_balance", label: "Saldo konta (account_balance)" },
  { value: "credit_card", label: "Karta kredytowa (credit_card)" },
  { value: "local_offer", label: "Oferta (local_offer)" },
  { value: "discount", label: "Rabat (discount)" },
  { value: "sell", label: "Sprzedaj (sell)" },
  { value: "price_change", label: "Zmiana ceny (price_change)" },
  { value: "savings", label: "Oszczędności (savings)" },

  // Schedule & Time
  { value: "schedule", label: "Harmonogram (schedule)" },
  { value: "event", label: "Wydarzenie (event)" },
  { value: "calendar_today", label: "Kalendarz dzisiaj (calendar_today)" },
  { value: "calendar_month", label: "Kalendarz miesiąc (calendar_month)" },
  { value: "access_time", label: "Czas dostępu (access_time)" },
  { value: "timer", label: "Timer (timer)" },
  { value: "alarm", label: "Alarm (alarm)" },
  { value: "watch_later", label: "Obejrzyj później (watch_later)" },

  // People & Users
  { value: "person", label: "Osoba (person)" },
  { value: "person_outline", label: "Osoba kontur (person_outline)" },
  { value: "group", label: "Grupa (group)" },
  { value: "people", label: "Ludzie (people)" },
  { value: "supervisor_account", label: "Konto nadzorcy (supervisor_account)" },
  { value: "account_circle", label: "Koło konta (account_circle)" },

  // Communication
  { value: "email", label: "Email (email)" },
  { value: "message", label: "Wiadomość (message)" },
  { value: "chat", label: "Chat (chat)" },
  { value: "forum", label: "Forum (forum)" },
  { value: "feedback", label: "Opinia (feedback)" },
  { value: "announcement", label: "Ogłoszenie (announcement)" },
  { value: "campaign", label: "Kampania (campaign)" },
  { value: "notifications", label: "Powiadomienia (notifications)" },

  // Status & Actions
  { value: "check", label: "Zaznacz (check)" },
  { value: "check_circle", label: "Zaznacz okrąg (check_circle)" },
  { value: "verified", label: "Zweryfikowane (verified)" },
  { value: "star", label: "Gwiazda (star)" },
  { value: "favorite", label: "Ulubione (favorite)" },
  { value: "thumb_up", label: "Kciuk w górę (thumb_up)" },
  { value: "done", label: "Gotowe (done)" },
  { value: "done_all", label: "Wszystko gotowe (done_all)" },

  // Warnings & Alerts
  { value: "warning", label: "Ostrzeżenie (warning)" },
  { value: "error", label: "Błąd (error)" },
  { value: "report_problem", label: "Zgłoś problem (report_problem)" },
  { value: "priority_high", label: "Wysoki priorytet (priority_high)" },

  // Technology & Tools
  { value: "laptop", label: "Laptop (laptop)" },
  { value: "computer", label: "Komputer (computer)" },
  { value: "phone", label: "Telefon (phone)" },
  { value: "smartphone", label: "Smartfon (smartphone)" },
  { value: "tablet", label: "Tablet (tablet)" },
  { value: "settings", label: "Ustawienia (settings)" },
  { value: "tune", label: "Dostrajanie (tune)" },
  { value: "build", label: "Budowa (build)" },
  { value: "construction", label: "Budowa (construction)" },
  { value: "engineering", label: "Inżynieria (engineering)" },

  // Security
  { value: "security", label: "Bezpieczeństwo (security)" },
  { value: "lock", label: "Zamek (lock)" },
  { value: "key", label: "Klucz (key)" },
  { value: "vpn_key", label: "Klucz VPN (vpn_key)" },
  { value: "admin_panel_settings", label: "Panel administracyjny (admin_panel_settings)" },

  // Location & Places
  { value: "location_on", label: "Lokalizacja (location_on)" },
  { value: "place", label: "Miejsce (place)" },
  { value: "home", label: "Dom (home)" },
  { value: "work", label: "Praca (work)" },
  { value: "business", label: "Biznes (business)" },
  { value: "apartment", label: "Mieszkanie (apartment)" },

  // Analytics & Charts
  { value: "analytics", label: "Analityka (analytics)" },
  { value: "insights", label: "Wglądy (insights)" },
  { value: "trending_up", label: "Trend wzrostowy (trending_up)" },
  { value: "trending_down", label: "Trend spadkowy (trending_down)" },
  { value: "show_chart", label: "Pokaż wykres (show_chart)" },
  { value: "bar_chart", label: "Wykres słupkowy (bar_chart)" },
  { value: "pie_chart", label: "Wykres kołowy (pie_chart)" },
  { value: "assessment", label: "Ocena (assessment)" },

  // Search & Visibility
  { value: "search", label: "Szukaj (search)" },
  { value: "find_in_page", label: "Znajdź na stronie (find_in_page)" },
  { value: "visibility", label: "Widoczność (visibility)" },
  { value: "visibility_off", label: "Widoczność wyłączona (visibility_off)" },
  { value: "remove_red_eye", label: "Usuń czerwone oko (remove_red_eye)" },

  // Global & Languages
  { value: "language", label: "Język (language)" },
  { value: "translate", label: "Tłumacz (translate)" },
  { value: "public", label: "Publiczne (public)" },
  { value: "travel_explore", label: "Podróże eksploruj (travel_explore)" },
  { value: "explore", label: "Eksploruj (explore)" },
];

interface MaterialIconSelectProps {
  name: string;
  label?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const IconOption = (props: any) => {
  const { data } = props;
  return (
    <div className="text-sm">
      <components.Option {...props} className="!flex !items-center !gap-1">
        {data.value && <div className="material-icons text-lg text-slate-700 dark:text-slate-300">{data.value}</div>}
        <div className="text-sm text-slate-700 dark:text-slate-300">{data.label}</div>
      </components.Option>
    </div>
  );
};

export const MaterialIconSelect = ({
  name,
  label = "Ikona",
  className,
  required = false,
  disabled = false,
  placeholder = "Wybierz ikonę...",
}: MaterialIconSelectProps): JSX.Element => {
  return (
    <PaziaSelect
      name={name}
      label={label}
      className={className}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      options={MATERIAL_ICONS}
      components={{
        Option: IconOption,
      }}
      isClearable={false}
    />
  );
};
